import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import { NumberInput, Slider } from "@mantine/core";
import { imgNbaLogo } from "../../../../assets";

export default function KYCStep3() {
  const navigate = useNavigate();
  const [professionalFee, setProfessionalFee] = useState("5000000");
  const [feePercentage, setFeePercentage] = useState(10);

  const declaredValue = 45000000;
  const fairMarketValue = 50000000;
  const minFee = Math.max(declaredValue, fairMarketValue) * 0.1;

  const handleFeeChange = (value: string) => {
    const numValue = parseInt(value.replace(/\D/g, "")) || 0;
    setProfessionalFee(numValue.toString());
    if (numValue > 0) {
      const percentage = (numValue / Math.max(declaredValue, fairMarketValue)) * 100;
      setFeePercentage(Math.max(10, Math.round(percentage)));
    }
  };

  const handleNext = () => {
    navigate("/d/kyc/step4");
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <div className="kyc-logo-section">
          <img className="kyc-logo-img" src={imgNbaLogo} alt="NBA Logo" />
          <div className="kyc-logo-text">
            <div className="kyc-logo-name">NIGERIAN BAR ASSOCIATION</div>
            <div className="kyc-logo-sub">REMUNERATION PORTAL</div>
          </div>
        </div>
      </div>

      <div className="kyc-progress">
        <button
          className="kyc-back-button"
          type="button"
          onClick={() => navigate("/d/kyc/step2")}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="kyc-progress-content">
          <span className="kyc-progress-text">Step 3 of 7</span>
          <div className="kyc-progress-bar">
            <div className="kyc-progress-fill" style={{ width: "42.9%" }} />
          </div>
        </div>
      </div>

      <div className="kyc-container">
        <div className="kyc-content">
          <div className="kyc-value-section">
            <div className="kyc-value-row">
              <span className="kyc-value-label">Declared Value</span>
              <span className="kyc-value-amount">₦{declaredValue.toLocaleString()}</span>
            </div>
            <div className="kyc-fair-value">
              Fair Market Value ₦{fairMarketValue.toLocaleString()}
            </div>
          </div>

          <div className="kyc-fee-section">
            <h2 className="kyc-section-title">Professional Fee</h2>
            <div className="kyc-fee-header">
              <span className="kyc-fee-label">Set Your Fee *</span>
              <span className="kyc-fee-min">Min: ₦{minFee.toLocaleString()}</span>
            </div>

            <NumberInput
              label="Set Your Fee *"
              placeholder="Enter professional fee"
              value={parseInt(professionalFee)}
              onChange={(value) => handleFeeChange(value?.toString() || "0")}
              prefix="₦"
              thousandSeparator=","
              min={minFee}
              description={`Min: ₦${minFee.toLocaleString()}`}
            />

            <div className="kyc-slider-section">
              <p className="kyc-slider-label">
                Adjust fee percentage (minimum 10%)
              </p>
              <Slider
                value={feePercentage}
                onChange={(value) => {
                  setFeePercentage(value);
                  const newFee = Math.round(
                    (Math.max(declaredValue, fairMarketValue) * value) / 100
                  );
                  setProfessionalFee(newFee.toString());
                }}
                min={10}
                max={100}
                marks={[
                  { value: 10, label: "10%" },
                  { value: 50, label: "50%" },
                  { value: 100, label: "100%" },
                ]}
              />
              <p className="kyc-slider-value">{feePercentage}% (Minimum)</p>
            </div>

            <div className="kyc-info-box kyc-info-box--with-icon">
              <Info size={20} />
              <p>
                The professional fee must be at least 10% of the higher value
                between Declared Value and Fair Market Value. You can set it
                higher based on transaction complexity.
              </p>
            </div>
          </div>

          <button
            className="dashboard-button dashboard-button--primary dashboard-button--large"
            type="button"
            onClick={handleNext}
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );
}

